'use client';

import { Lock, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type LockTier = 'user' | 'pro';

/**
 * The chip that says why a filter is out of reach, and what unlocks it.
 *
 * Both variants are deliberately coloured. The sign-in chip was originally
 * `bg-muted text-muted-foreground`, which is the same grey the sidebar uses
 * for placeholders and disabled fields — so on a phone it read as "this
 * control is broken" rather than "there is something to unlock here". Sky
 * for free, amber for paid: two invitations, two prices.
 */
export function FilterLockBadge({
  lock,
  className,
  iconOnly = false,
}: {
  lock: LockTier;
  className?: string;
  iconOnly?: boolean;
}) {
  const { t } = useTranslation();
  const isPro = lock === 'pro';

  return (
    <span
      aria-hidden={iconOnly || undefined}
      className={cn(
        'inline-flex shrink-0 items-center rounded-full ring-1 ring-inset',
        iconOnly
          ? 'size-3.5 justify-center'
          : 'gap-0.5 px-1.5 py-px text-[10px] font-medium leading-none',
        isPro
          ? 'bg-amber-100 text-amber-800 ring-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-800'
          : 'bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-950 dark:text-sky-300 dark:ring-sky-800',
        className,
      )}
    >
      {isPro ? <Star size={9} className="fill-current" /> : <Lock size={9} />}
      {!iconOnly && (isPro
        ? t('search.sidebar.proBadge', { defaultValue: 'Pro' })
        : t('search.sidebar.loginBadge', { defaultValue: 'Sign in' }))}
    </span>
  );
}

/**
 * The tooltip that goes with a lock chip.
 *
 * `scope: 'group'` phrases it in the plural — a locked group header stands
 * in for every filter behind it, not for one.
 */
export function useLockHint(): (
  lock: LockTier,
  scope?: 'filter' | 'group',
) => string {
  const { t } = useTranslation();
  return (lock, scope = 'filter') => {
    if (scope === 'group') {
      return lock === 'pro'
        ? t('search.sidebar.groupProHint', {
            defaultValue: 'Available on Pro — upgrade to use these filters',
          })
        : t('search.sidebar.groupLoginHint', {
            defaultValue: 'Sign in for free to use these filters',
          });
    }
    return lock === 'pro'
      ? t('search.sidebar.proOnlyHint', {
          defaultValue: 'Available on Pro — upgrade to use this filter',
        })
      : t('search.sidebar.loginOnlyHint', {
          defaultValue: 'Sign in for free to use this filter',
        });
  };
}
